import React, { useState } from "react";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import {
    Ticket,
    ShieldCheck,
    BarChart3,
    Smartphone,
    ScanLine,
    Check,
    ArrowRight,
    Sparkles,
    Users,
    ChevronDown,
    LayoutDashboard,
    Receipt,
    Tag,
    Send,
} from "lucide-react";

const PILLARS = [
    {
        title: "Expérience fluide pour tous",
        text: "Profitez d’une expérience fluide sur tous les écrans, que vous soyez en train de configurer votre billetterie d’événement ou d’acheter un billet.",
    },
    {
        title: "Mise en ligne rapide",
        text: "Créez votre événement, définissez vos tarifs et visuels sans compétences techniques avancées — vous vendez quand vous êtes prêt.",
    },
    {
        title: "Accompagnement",
        text: "Que vous ayez besoin d’aide pour les réglages ou pour choisir les meilleures options pour votre événement, notre équipe qualifiée est là pour vous guider. Nous sommes disponibles via téléphone, e-mail et chat (whatsapp) pour assurer votre succès à chaque étape.",
    },
    {
        title: "Gérez vos participants",
        text: "Suivez les ventes et les inscriptions en temps réel depuis le mobile ou ordinateur, votre tableau de bord vous guide dans la gestion de votre événement.",
    },
    {
        title: "Suivi en temps réel",
        text: "Consultez les performances de votre événement en temps réel grâce à une interface claire et intuitive. Les informations sont actualisées instantanément, vous permettant de rester toujours à jour.",
    },
    {
        title: "Export facile des données",
        text: "Exportez facilement les données de votre événement en format CSV ou Excel pour vos rapports ou vos partenaires, et suivez l’évolution de vos ventes.",
    },
    {
        title: "Automatisations",
        text: "Automatisez des tâches répétitives pour gagner du temps et améliorer vos processus d’organisation.",
    },
];

const SECURITY = [
    "Des données collectées pour vous servir : pas de revente à des tiers.",
    "Toutes les transactions sont conformes aux normes de sécurité.",
    "Le transfert des données est effectué en toute sécurité grâce au protocole SSL.",
];

const ORGANIZER_FEATURES = [
    {
        icon: LayoutDashboard,
        title: "Espace organisateur",
        // text: "Un tableau de bord unique pour créer vos événements, suivre l’activité et accéder aux outils clés sans multiplier les interfaces.",
    },
    {
        icon: BarChart3,
        title: "Suivi des ventes et rapports",
        // text: "Visualisez l’évolution des commandes, identifiez les tarifs les plus performants et ajustez votre communication en temps réel.",
    },
    {
        icon: Ticket,
        title: "Billetterie & tarifs",
        // text: "Définissez vos formules, quotas et périodes de vente, puis mettez à jour vos offres avant ou pendant la billetterie.",
    },
    {
        icon: Users,
        title: "Participants & listes",
        // text: "Consultez vos acheteurs, préparez l’accueil et exploitez les informations collectées pour le jour J.",
    },
    {
        icon: ScanLine,
        title: "Contrôle d’accès",
        // text: "Des billets cohérents avec la validation à l’entrée : fluidifiez les files et sécurisez les accès.",
    },
    {
        icon: Receipt,
        title: "Facturation",
        // text: "Retrouvez vos éléments de facturation côté organisateur pour vos comptes et vos partenariats.",
    },
    {
        icon: Tag,
        title: "Codes promo",
        // text: "Créez des réductions ciblées pour booster les ventes, fidéliser un segment ou accompagner une campagne partenaire.",
    },
    {
        icon: Send,
        title: "Envoi d'invitations",
        // text: "Invitez des contacts par e-mail pour les orienter vers votre billetterie ou une offre dédiée, et suivez qui a reçu le message.",
    }
];

const PILLAR_IMAGE_DEFAULT = "images/mainpage-organizer-dashbord.png";
const PILLAR_IMAGE_PARTICIPANTS = "images/billetterie-offer-participants-list.png";
/** Index de l’entrée « Gérez vos participants » dans PILLARS */
const PILLAR_INDEX_PARTICIPANTS = 3;

export default function BilleterieOffer() {
    const imagePath = process.env.REACT_APP_IMAGE_PATH;
    const [pillarOpen, setPillarOpen] = useState(0);
    const canonicalUrl = "https://eventsbilletterie.com/offre-billetterie";
    const seoTitle = "Offre billetterie evenementielle en ligne | Events billetterie";
    const seoDescription =
        "Solution complete de billetterie evenementielle : creation d'evenements, inscriptions, paiement securise, controle d'acces, billets physiques et bracelets pour organisateurs.";
    const seoKeywords =
        "offre billetterie, evenement, billetterie, billetterie en ligne, billetterie securise, ticket facile,billetterie evenementielle, billetterie en ligne cote d'ivoire, vente de tickets evenement, gestion inscriptions, controle d'acces qr code, billets physiques evenement, bracelets unique, bracelets securises, ticket unique, ticket unique cote d'ivoire, ticket securise, ticket infalsifiable, ticket qr code, bracelet qr code, qr code unique";
    const seoImage = "https://eventsbilletterie.com/assets/images/event-details-dash.png";
    const billetterieOfferSeoJsonLd = {
        "@context": "https://schema.org",
        "@type": "Service",
        serviceType: "Billetterie evenementielle",
        name: seoTitle,
        description: seoDescription,
        areaServed: {
            "@type": "Country",
            name: "Cote d'Ivoire",
        },
        provider: {
            "@type": "Organization",
            name: "Events Billetterie",
            url: "https://eventsbilletterie.com",
        },
        url: canonicalUrl,
        inLanguage: "fr-CI",
    };

    const togglePillar = (index) => {
        setPillarOpen((prev) => (prev === index ? null : index));
    };

    const showParticipantsPreview = pillarOpen === PILLAR_INDEX_PARTICIPANTS;
    const pillarPreviewSrc = `${imagePath}${showParticipantsPreview ? PILLAR_IMAGE_PARTICIPANTS : PILLAR_IMAGE_DEFAULT}`;
    const pillarPreviewAlt = showParticipantsPreview
        ? "Liste des participants et aperçu du profil dans l’espace organisateur Events"
        : "Tableau de bord organisateur et aperçu de la billetterie Events";

    return (
        <main className="nc-PageAbout relative overflow-hidden">
            <Helmet>
                <html lang="fr" />
                <title>{seoTitle}</title>
                <meta name="description" content={seoDescription} />
                <meta name="keywords" content={seoKeywords} />
                <meta
                    name="robots"
                    content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1"
                />
                <meta property="og:type" content="website" />
                <meta property="og:locale" content="fr_CI" />
                <meta property="og:site_name" content="Events Billetterie" />
                <meta property="og:title" content={seoTitle} />
                <meta property="og:description" content={seoDescription} />
                <meta property="og:url" content={canonicalUrl} />
                <meta property="og:image" content={seoImage} />
                <meta name="twitter:card" content="summary_large_image" />
                <meta name="twitter:title" content={seoTitle} />
                <meta name="twitter:description" content={seoDescription} />
                <meta name="twitter:image" content={seoImage} />
                <meta name="twitter:site" content="@events_billetterie" />
                <link rel="canonical" href={canonicalUrl} />
                <script type="application/ld+json">
                    {JSON.stringify(billetterieOfferSeoJsonLd)}
                </script>
            </Helmet>

            <div className="nc-BgGlassmorphism pointer-events-none absolute inset-x-0 top-0 min-h-[24rem] pl-20 py-24 flex overflow-hidden z-0">
                <span className="block bg-[#ef233c]/20 w-72 h-72 rounded-full mix-blend-multiply filter blur-3xl opacity-30 lg:w-96 lg:h-96" />
                <span className="block bg-[#04868b]/20 w-72 h-72 -ml-20 mt-24 rounded-full mix-blend-multiply filter blur-3xl opacity-25 lg:w-96 lg:h-96" />
            </div>

            <div className="container relative z-[1] px-4 pt-12 pb-20 md:pt-16 md:pb-28">
                {/* Hero — inspiration structure type Weezevent : promesse + preuves rapides */}
                <div className="max-w-4xl mx-auto text-center">
                    <div className="inline-flex items-center gap-2 rounded-full bg-primary-6000/10 text-primary-700 dark:text-primary-400 px-4 py-1.5 text-sm font-medium mb-6">
                        <Ticket className="w-4 h-4" aria-hidden />
                        Billetterie &amp; inscriptions
                    </div>
                    <h1 className="text-5xl md:text-5xl lg:text-5xl font-bold text-neutral-900 dark:text-neutral-50 tracking-tight">
                        Lancez votre{" "}
                        <span className="text-primary-6000">billetterie en ligne</span> en quelques étapes
                    </h1>
                    <p className="mt-6 text-lg md:text-xl text-neutral-600 dark:text-neutral-400 leading-relaxed max-w-2xl mx-auto">
                        Personnalisez votre page événement, vendez vos billets et gérez vos inscriptions depuis une seule
                        plateforme — pour offrir une expérience simple à vos participants et garder le contrôle sur vos
                        ventes.
                    </p>
                    <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-4">
                        <Link
                            to="/dashboard/events/registration"
                            className="inline-flex items-center justify-center gap-2 rounded-lg bg-primary-6000 hover:bg-primary-700 text-white font-semibold px-8 py-2 text-sm md:text-base shadow-lg shadow-primary-6000/25 transition-colors"
                        >
                            Ouvrir ma billetterie maintenant
                            <ArrowRight className="w-4 h-4" aria-hidden />
                        </Link>
                        <Link
                            to="/organisateurs/devenir-organisateur-events"
                            className="inline-flex items-center justify-center rounded-lg border-2 border-neutral-300 dark:border-neutral-600 text-neutral-800 dark:text-neutral-200 font-semibold px-6 py-2 text-sm hover:bg-neutral-100 dark:hover:bg-neutral-800 transition-colors"
                        >
                            Devenir organisateur
                        </Link>
                    </div>
                    <ul className="mt-8 flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-sm text-neutral-600 dark:text-neutral-400">
                        <li className="inline-flex items-center gap-1.5">
                            <Check className="w-4 h-4 text-emerald-700 shrink-0" aria-hidden />
                            Sans installation logicielle
                        </li>
                        <li className="inline-flex items-center gap-1.5">
                            <Check className="w-4 h-4 text-emerald-700 shrink-0" aria-hidden />
                            Parcours d’inscription clair
                        </li>
                        <li className="inline-flex items-center gap-1.5">
                            <Check className="w-4 h-4 text-emerald-700 shrink-0" aria-hidden />
                            Accompagnement Dédié
                        </li>
                    </ul>
                </div>

                <div className="mt-14 max-w-5xl mx-auto flex justify-center">
                    <img
                        className="max-w-full sm:!max-w-[30rem] h-auto rounded-xl shadow-md"
                        alt=""
                        loading="lazy"
                        decoding="async"
                        data-nimg={1}
                        style={{ color: "transparent",  }}
                        src={`${imagePath}images/event-details-dash.png`}
                    />
                </div>

                {/* Piliers — image + accordéon */}
                <section
                    className="mt-40 md:mt-32 max-w-6xl mx-auto"
                    aria-labelledby="heading-pillars-billetterie"
                >
                    <div className="grid lg:grid-cols-2 gap-10 lg:gap-12 items-center justify-center">
                        <figure className="order-2 lg:order-1 rounded-3xl p-4 sm:p-5 overflow-hidden">
                            <img
                                key={showParticipantsPreview ? "participants" : "default"}
                                className="w-full h-auto rounded-2xl object-cover transition-opacity duration-300"
                                alt={pillarPreviewAlt}
                                loading="lazy"
                                decoding="async"
                                src={pillarPreviewSrc}
                            />
                        </figure>

                        <div className="order-1 lg:order-2 min-w-0">
                            <h2
                                id="heading-pillars-billetterie"
                                className="text-2xl md:text-3xl font-bold text-neutral-900 dark:text-neutral-50 text-center lg:text-left"
                            >
                                Une solution de billetterie pensée pour vous et vos participants
                            </h2>
                            <p className="mt-3 text-neutral-600 dark:text-neutral-400 text-center lg:text-left max-w-xl mx-auto lg:mx-0">
                            Découvrez la plateforme de billetterie conçue pour simplifier votre expérience et celle de vos participants.
                            </p>

                            <div className="mt-8 space-y-3">
                                {PILLARS.map(({ title, text }, index) => {
                                    const isOpen = pillarOpen === index;
                                    const panelId = `pillar-panel-${index}`;
                                    const headerId = `pillar-header-${index}`;
                                    return (
                                        <div
                                            key={title}
                                            className="rounded-2xl border border-neutral-200/90 dark:border-neutral-700 bg-white/90 dark:bg-neutral-900/50 shadow-sm overflow-hidden"
                                        >
                                            <h3 className="text-base m-0">
                                                <button
                                                    type="button"
                                                    id={headerId}
                                                    className="flex w-full items-center gap-3 p-4 text-left hover:bg-neutral-50/80 dark:hover:bg-neutral-800/50 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-6000 focus-visible:ring-offset-2 dark:focus-visible:ring-offset-neutral-900"
                                                    onClick={() => togglePillar(index)}
                                                    aria-expanded={isOpen}
                                                    aria-controls={panelId}
                                                >
                                                    <span className="flex-1 font-semibold text-neutral-900 dark:text-neutral-100 pr-2">
                                                        {title}
                                                    </span>
                                                    <ChevronDown
                                                        className={`h-5 w-5 shrink-0 text-neutral-500 transition-transform duration-200 ${
                                                            isOpen ? "rotate-180" : ""
                                                        }`}
                                                        aria-hidden
                                                    />
                                                </button>
                                            </h3>
                                            {isOpen && (
                                                <div
                                                    id={panelId}
                                                    role="region"
                                                    aria-labelledby={headerId}
                                                    className="border-t border-neutral-200/80 dark:border-neutral-700"
                                                >
                                                    <p className="px-4 pb-4 pt-3 pl-[4.5rem] text-neutral-600 dark:text-neutral-400 leading-relaxed">
                                                        {text}
                                                    </p>
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                                <Link
                                    to="/dashboard/events/registration"
                                    className="inline-flex items-center justify-center gap-2 rounded-lg bg-gray-800 hover:bg-gray-700 text-white font-semibold px-8 py-2 mt-4 text-sm md:text-base shadow-lg shadow-primary-6000/25 transition-colors"
                                >
                                    Demarrez dès maintenant
                                    <ArrowRight className="w-4 h-4" aria-hidden />
                                </Link>
                            </div>
                        </div>
                    </div>
                </section>

                {/* Fonctionnalités organisateurs */}
                <section
                    className="mt-24 md:mt-32 max-w-6xl mx-auto"
                    aria-labelledby="heading-organizer-features"
                >
                    <div className="rounded-3xl border border-neutral-200 dark:border-neutral-700 p-8 md:p-12">
                        <h2
                            id="heading-organizer-features"
                            className="text-2xl md:text-3xl font-bold text-neutral-900 dark:text-neutral-50"
                        >
                            Des fonctionnalités pour tout type d'événement
                        </h2>
                        <ul className="mt-8 grid sm:grid-cols-2 lg:grid-cols-4 gap-6 list-none p-0 m-0">
                            {ORGANIZER_FEATURES.map(({ icon: Icon, title }) => (
                                <li key={title} className="flex gap-3 items-center">
                                    <div className="shrink-0 mt-0.5 rounded-lg bg-gray-800/10 p-2 text-gray-800">
                                        <Icon className="w-5 h-5" strokeWidth={2} aria-hidden />
                                    </div>
                                    <div>
                                        <h3 className="font-semibold text-neutral-900 dark:text-neutral-100">{title}</h3>
                                    </div>
                                </li>
                            ))}
                        </ul>
                        {/* Sécurité */}
                        <div className="mt-24 max-w-6xl mx-auto rounded-3xl bg-emerald-50/50 dark:bg-emerald-950/20 p-8 md:p-10">
                            <div className="flex flex-col md:flex-row md:items-start gap-6">
                                <div className="shrink-0 rounded-2xl bg-emerald-600/15 p-4 text-emerald-700 dark:text-emerald-400">
                                    <ShieldCheck className="w-10 h-10" strokeWidth={1.5} aria-hidden />
                                </div>
                                <div>
                                    <h2 className="text-xl md:text-2xl font-bold text-neutral-900 dark:text-neutral-50">
                                        Une billetterie sérieuse et fiable
                                    </h2>
                                    <p className="mt-2 text-neutral-600 dark:text-neutral-400">
                                    La protection de vos données est au cœur de notre engagement ! Collectez et vendez vos billets en toute sécurité.
                                    </p>
                                    <ul className="mt-6 space-y-2">
                                        {SECURITY.map((line) => (
                                            <li key={line} className="flex items-start gap-2 text-sm text-neutral-700 dark:text-neutral-300">
                                                <Check className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5" aria-hidden />
                                                {line}
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                {/* Billets physiques sécurisés */}
                <section
                    className="mt-24 md:mt-32 max-w-6xl mx-auto"
                    aria-labelledby="heading-ticket-physique"
                >
                    <div className="grid lg:grid-cols-2 gap-10 lg:gap-12 items-center">
                        <figure className="bg-white/90 dark:bg-neutral-900/50 p-4 sm:p-5 rounded-3xl">
                            <img
                                className="w-full h-auto max-h-[min(70vh,28rem)] mx-auto object-contain rounded-2xl"
                                alt="Exemple de billet physique avec talon, perforation et QR code"
                                loading="lazy"
                                decoding="async"
                                src={`${imagePath}images/billetterie-offer-ticket-physique-securise.png`}
                            />
                        </figure>
                        <div className="min-w-0">
                            <h2
                                id="heading-ticket-physique"
                                className="scroll-mt-28 text-2xl md:text-3xl font-bold text-neutral-900 dark:text-neutral-50"
                            >
                                Edition & Impression de billets physiques sécurisés
                            </h2>
                            <p className="mt-3 text-neutral-600 dark:text-neutral-400 leading-relaxed">
                                Offrez des entrées papier au rendu professionnel, difficiles à reproduire : finitions nettes,
                                talon perforé et QR pour un contrôle rapide à l’entrée. Idéal pour les files d’attente, les
                                partenaires ou le public qui préfère un support tangible.
                            </p>
                            <ul className="mt-6 space-y-3 list-none p-0 m-0">
                                <li className="flex gap-3 text-sm text-neutral-700 dark:text-neutral-300">
                                    <Check className="w-5 h-5 text-primary-6000 shrink-0 mt-0.5" aria-hidden />
                                    <span>Sécurisation et traçabilité, 100% garantie.</span>
                                </li>
                                <li className="flex gap-3 text-sm text-neutral-700 dark:text-neutral-300">
                                    <Check className="w-5 h-5 text-primary-6000 shrink-0 mt-0.5" aria-hidden />
                                    <span>QR et repères visuels pour limiter la contrefaçon et accélérer la validation.</span>
                                </li>
                                <li className="flex gap-3 text-sm text-neutral-700 dark:text-neutral-300">
                                    <Check className="w-5 h-5 text-primary-6000 shrink-0 mt-0.5" aria-hidden />
                                    <span>Compatible avec une organisation sur site : équipes, accès et zones.</span>
                                </li>
                            </ul>
                        </div>
                    </div>
                </section>

                {/* Bracelets uniques */}
                <section
                    className="mt-24 md:mt-32 max-w-6xl mx-auto"
                    aria-labelledby="heading-bracelet-unique"
                >
                    <div className="grid lg:grid-cols-2 gap-10 lg:gap-12 items-center">
                        <div className="min-w-0 order-2 lg:order-1">
                            <h2
                                id="heading-bracelet-unique"
                                className="scroll-mt-28 text-2xl md:text-3xl font-bold text-neutral-900 dark:text-neutral-50"
                            >
                                Bracelets uniques pour vos participants
                            </h2>
                            <p className="mt-3 text-neutral-600 dark:text-neutral-400 leading-relaxed">
                                Renforcez l’identification sur le terrain avec des bracelets personnalisables : visuel fort,
                                port confortable. Pensés pour le contrôle d’accès et la
                                reconnaissance des profils (VIP, staff, public) en un coup d’œil.
                            </p>
                            <ul className="mt-6 space-y-3 list-none p-0 m-0">
                                <li className="flex gap-3 text-sm text-neutral-700 dark:text-neutral-300">
                                    <Check className="w-5 h-5 text-primary-6000 shrink-0 mt-0.5" aria-hidden />
                                    <span>Support unique, sans risque de duplication ou fraude 100% sécurisé avec QR code.</span>
                                </li>
                                <li className="flex gap-3 text-sm text-neutral-700 dark:text-neutral-300">
                                    <Check className="w-5 h-5 text-primary-6000 shrink-0 mt-0.5" aria-hidden />
                                    <span>Conception robuste et durable, résistante aux conditions extérieures.</span>
                                </li>
                                <li className="flex gap-3 text-sm text-neutral-700 dark:text-neutral-300">
                                    <Check className="w-5 h-5 text-primary-6000 shrink-0 mt-0.5" aria-hidden />
                                    <span>Complément idéal des billets numériques ou papier pour le jour J.</span>
                                </li>
                            </ul>
                        </div>
                        <figure className="order-1 lg:order-2 rounded-3xl bg-white/90 dark:bg-neutral-900/50 p-4 sm:p-5">
                            <img
                                className="w-full h-auto max-h-[min(60vh,20rem)] mx-auto object-contain rounded-2xl"
                                alt="Bracelet événement tissé avec plaque participant personnalisable"
                                loading="lazy"
                                decoding="async"
                                src={`${imagePath}images/billetterie-offer-bracelet-unique.png`}
                            />
                        </figure>
                    </div>
                </section>

                {/* Écosystème Events */}
                <section className="mt-24 max-w-6xl mx-auto">
                    <h2 className="text-2xl md:text-3xl font-bold text-center text-neutral-900 dark:text-neutral-50">
                        Plus qu’une billetterie : tout l’écosystème Events
                    </h2>
                    <p className="mt-3 text-center text-neutral-600 dark:text-neutral-400 max-w-2xl mx-auto">
                        Combinez votre page web avec l’application mobile et les outils de contrôle pour une expérience
                        bout en bout.
                    </p>
                    <ul className="mt-10 grid md:grid-cols-3 gap-6 list-none p-0 m-0">
                        <li className="rounded-2xl border border-neutral-200 dark:border-neutral-700 p-6 text-center">
                            <Smartphone className="w-8 h-8 mx-auto text-primary-6000" aria-hidden />
                            <h3 className="mt-4 font-semibold text-neutral-900 dark:text-neutral-100">Application mobile</h3>
                            <p className="mt-2 text-sm text-neutral-600 dark:text-neutral-400">
                                Vos événements visibles là où le public consulte le plus souvent.
                            </p>
                            <Link
                                to="/application-mobile"
                                className="mt-4 inline-flex items-center gap-1 text-sm font-medium text-primary-6000 hover:underline"
                            >
                                Découvrir <ArrowRight className="w-3.5 h-3.5" />
                            </Link>
                        </li>
                        <li className="rounded-2xl border border-neutral-200 dark:border-neutral-700 p-6 text-center">
                            <ScanLine className="w-8 h-8 mx-auto text-primary-6000" aria-hidden />
                            <h3 className="mt-4 font-semibold text-neutral-900 dark:text-neutral-100">Contrôle d’accès</h3>
                            <p className="mt-2 text-sm text-neutral-600 dark:text-neutral-400">
                                Des billets cohérents avec la validation à l’entrée.
                            </p>
                            <Link
                                to="/controle-acces"
                                className="mt-4 inline-flex items-center gap-1 text-sm font-medium text-primary-6000 hover:underline"
                            >
                                En savoir plus <ArrowRight className="w-3.5 h-3.5" />
                            </Link>
                        </li>
                        <li className="rounded-2xl border border-neutral-200 dark:border-neutral-700 p-6 text-center">
                            <Sparkles className="w-8 h-8 mx-auto text-primary-6000" aria-hidden />
                            <h3 className="mt-4 font-semibold text-neutral-900 dark:text-neutral-100">Email & Communicatios</h3>
                            <p className="mt-2 text-sm text-neutral-600 dark:text-neutral-400">
                            Renforcez l’engagement et fidélisez vos participants avec nos outils de communication.
                            </p>
                            <Link
                                to="/communication-evenementielle"
                                className="mt-4 inline-flex items-center gap-1 text-sm font-medium text-primary-6000 hover:underline"
                            >
                                En savoir plus <ArrowRight className="w-3.5 h-3.5" />
                            </Link>
                        </li>
                    </ul>
                </section>

                {/* CTA final */}
                <div className="mt-24 max-w-3xl mx-auto text-center rounded-3xl bg-primary-6000 text-white px-8 py-12 md:py-14">
                    <h2 className="text-2xl md:text-3xl font-bold">Prêt à lancer votre billetterie en ligne ?</h2>
                    <p className="mt-3 text-white/90 text-sm md:text-base">
                        Créez votre billetterie ou contactez-nous pour un échange sur votre projet.
                    </p>
                    <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-4">
                        <Link
                            to="/dashboard/events/registration"
                            className="inline-flex items-center justify-center gap-2 rounded-full bg-white text-primary-6000 font-semibold px-8 py-2 text-sm hover:bg-neutral-100 transition-colors w-full sm:w-auto"
                        >
                            Créer ma billetterie
                            <ArrowRight className="w-4 h-4" />
                        </Link>
                        <Link
                            to="/nous-contacter"
                            className="inline-flex items-center justify-center rounded-full border-2 border-white/80 text-white font-semibold px-6 py-2 text-sm hover:bg-white/10 w-full sm:w-auto"
                        >
                            Nous contacter
                        </Link>
                    </div>
                </div>
            </div>
        </main>
    );
}
